import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Feature, PlanPeriodSummary, getBenefitSummaryApi } from 'core'
import {
  EmployeeHSAStatusChanges,
  getUserHSAStatusByYear,
  updateHSAStatus,
} from 'core/application/employees/employeeHSAStatus'
import CompanyUserHSAStatus from 'core/domain/companies/companyUserHSAStatus'

import { Avatar, Button, Card, Col, Icon, Progress, Row, Skeleton } from 'common/components'
import { formatYear } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'
import { formatMoney } from 'common/moneyFormatter'
import { ROUTE_PATHS } from 'common/routePaths'

import useFeatureFlag from '../../../common/hooks/useFeatureFlag'
import ContributeToHsaModal from '../../groupHra/home/summary/benefitSummary/contributeToHsaModal'

type CurrentBalanceProps = {
  planPeriodId: string
  reimburseButtonDisabled?: boolean
}

const CurrentBalance = ({ planPeriodId, reimburseButtonDisabled }: CurrentBalanceProps) => {
  const { t: translate } = useTranslation()
  const history = useHistory()
  const [contributeToHsaModalVisible, setContributeToHsaModalVisible] = useState(false)
  const [userContributesToHsa, setUserContributesToHsa] = useState<boolean>()
  const { sendCommand } = useCommandRequestV2((arg: EmployeeHSAStatusChanges) => updateHSAStatus(arg))
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState(null)

  const { isLoading: isUpdateHsaLoading, sendQuery } = useQueryRequestV2<CompanyUserHSAStatus>({
    initialState: null,
  })

  const {
    data: balances,
    sendQuery: sendGetAllowanceSummary,
    isLoading: balancesLoading,
  } = useQueryRequestV2<PlanPeriodSummary[]>({})

  useEffect(() => {
    ;(async () => {
      if (planPeriodId) {
        await sendGetAllowanceSummary(() => getBenefitSummaryApi(planPeriodId))
      }
    })()
  }, [planPeriodId])

  const canAddHSAStatus = useFeatureFlag(Feature.ShowHSAModalOnGetReimbursed)

  const handleGetReimbursedModalNextClicked = async (benefit: PlanPeriodSummary) => {
    const endYear = benefit.endDate ? parseInt(formatYear(benefit.endDate), 10) : null

    const result = await sendCommand({
      contributesToHSA: userContributesToHsa,
      year: endYear,
    })
    if (result.completed) {
      history.push(`${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_GET_REIMBURSED}/${selectedPlanPeriod.planPeriodId}`)
    }
  }

  const handleReimbursedModalCloseClicked = () => {
    history.push(`${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_GET_REIMBURSED}/${selectedPlanPeriod.planPeriodId}`)
  }

  const handleGetReimbursedClicked = async (benefit: PlanPeriodSummary) => {
    const year = formatYear(benefit.startDate)
    setSelectedPlanPeriod(benefit)
    const result = await sendQuery(() => getUserHSAStatusByYear(year))
    if (result || !canAddHSAStatus) {
      history.push(`${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_GET_REIMBURSED}/${benefit.planPeriodId}`)
    } else {
      setContributeToHsaModalVisible(true)
    }
  }

  return (
    <div>
      <Skeleton loading={balancesLoading}>
        {balances &&
          balances.map((balance) => (
            <Card key={balance.planPeriodId} className="mb-16 t-balance">
              <div className="pb-8 flex flex__align_center color-gray-8">
                <Icon type="far fa-calendar-alt" fontAwesome className="mr-4" />
                <div className={'t-date'}>
                  {translate('admin-dashboard.current-balance.plan-year')}: {formatYear(balance.startDate)}
                </div>
              </div>
              <h4 className="color-gray-9 text-semi-bold"> {translate('admin-dashboard.current-balance.header')} </h4>
              <Row justify="space-between">
                <Col className="flex flex__align_center">
                  <Row gutter={16}>
                    <Col>
                      <Avatar
                        size="large"
                        className="center_avatar"
                        icon={<Icon className="far fa-money-bill color-green-6" />}
                        style={{
                          backgroundColor: '#E6FFEE',
                          width: '64px',
                          height: '64px',
                        }}
                      />
                    </Col>

                    <Col>
                      <h4 className="color-gray-9 text-bold pt-16 mb-4 large-font-size t-availableBalance">
                        {formatMoney(balance.availableBalance < 0 ? 0 : balance.availableBalance)}
                      </h4>
                    </Col>
                  </Row>
                </Col>
                {balance.daysLeftForRequestingReimbursement && (
                  <Col>
                    <Progress
                      type="circle"
                      percent={(balance.daysLeftForRequestingReimbursement * 100) / 90}
                      format={() => (
                        <>
                          <h5 className="days_left_number">{balance.daysLeftForRequestingReimbursement}</h5>
                          <h6 className="days_left_label">{translate('admin-dashboard.current-balance.days-left')}</h6>
                        </>
                      )}
                      strokeColor="#F46A40"
                      strokeLinecap="square"
                      strokeWidth={8}
                      className={'t-progress'}
                    />
                  </Col>
                )}
              </Row>

              <Row>
                <Col span={24} className="login--login-button pt-16 pb-16">
                  <Button
                    icon="fa fa-plus"
                    iconPosition="left"
                    onClick={() => handleGetReimbursedClicked(balance)}
                    htmlType="submit"
                    loading={isUpdateHsaLoading}
                    type="primary"
                    className="button__large t-get-reimbursed"
                    data-testid={'get-reimbursed-cta'}
                    disabled={reimburseButtonDisabled}
                  >
                    Get reimbursed
                  </Button>
                </Col>
              </Row>
            </Card>
          ))}
      </Skeleton>
      {contributeToHsaModalVisible && (
        <ContributeToHsaModal
          visible={contributeToHsaModalVisible}
          planPeriodSummary={selectedPlanPeriod}
          onContributeToHsaChanged={(value) => {
            setUserContributesToHsa(value)
          }}
          onSubmitForm={() => handleGetReimbursedModalNextClicked(selectedPlanPeriod)}
          onCancel={() => handleReimbursedModalCloseClicked()}
          onBack={() => setContributeToHsaModalVisible(false)}
        />
      )}
    </div>
  )
}

export default CurrentBalance
